import { useCallback } from 'react';
import Build from '../core/Build';
import { useGeneratorContext } from '../contexts/GeneratorStateContext';
import { findAvailableSlotForItem, getSlotIdForItemType, getSlotImg } from '../core/buildUtils';
import { useNotification } from '../components/NotificationSystem';
import { useOverlay } from '../contexts/OverlayContext';
import SlotSelectOverlay from '../components/build/SlotSelectOverlay';
import { resolveMediaUrl } from '../utils/mediaUrl';
import MediaImg from '../components/utils/MediaImg';

const TXT_SLOT_SELECTION = { fr: 'Choix de l\'emplacement', en: 'Slot selection', de: 'Slot-Auswahl', es: 'Selección de espacio', pt: 'Seleção de espaço' };
const TXT_SETNAME_SET_ALREADY_EQUIPPED = { fr: '${setName || \'Panoplie\'} déjà équipée', en: '${setName || \'Set\'} already equipped', de: '${setName || \'Set\'} bereits ausgerüstet', es: '${setName || \'Set\'} ya equipado', pt: '${setName || \'Conjunto\'} já equipado' };

const DEFAULT_LVL = 200;

const MSG_ITEM_EQUIPPED = { fr: 'équipé', en: 'equipped', de: 'ausgerüstet', es: 'equipado', pt: 'equipado' };
const MSG_SET_EQUIPPED = { fr: 'équipée', en: 'equipped', de: 'ausgerüstet', es: 'equipada', pt: 'equipada' };
const MSG_PIECES = { fr: 'pièces', en: 'pieces', de: 'Teile', es: 'piezas', pt: 'peças' };
const MSG_EQUIP_ERROR = { fr: 'Erreur lors de l\'équipement', en: 'Error equipping item', de: 'Fehler beim Ausrüsten', es: 'Error al equipar', pt: 'Erro ao equipar' };
const MSG_ITEM_LEVEL_TOO_HIGH = { fr: 'Niveau trop élevé', en: 'Level too high', de: 'Stufe zu hoch', es: 'Nivel demasiado alto', pt: 'Nível muito alto' };
const MSG_SET_PARTIAL_LEVEL = { fr: 'non équipé(s) (niveau)', en: 'not equipped (level)', de: 'nicht ausgerüstet (Stufe)', es: 'no equipado(s) (nivel)', pt: 'não equipado(s) (nível)' };
const EMPTY_ID = '';
const EMPTY_LIST = [];
const MULTI_SLOT_COUNT = 1;
const ZERO_COUNT = 0;

const getLocalizedName = (name, lang) => {
    if (name && typeof name === 'object') {
        return name[lang] || name.fr || name.en || '';
    }
    return name || '';
};

const normalizeId = (id) => (id !== undefined && id !== null ? String(id) : EMPTY_ID);

const hasItemInBuild = (items, itemId) => {
    const normalizedId = normalizeId(itemId);
    if (!normalizedId) return false;
    return items.some(buildItem => normalizeId(buildItem?.id) === normalizedId);
};

const getItemName = (item, lang) => getLocalizedName(item?.name, lang);
const getSetName = (set, lang) => getLocalizedName(set?.name, lang);
const getItemLevel = (item) => item?.level || item?.lvl || item?.lvl_min || 0;

const getBaseBuild = (build) => build || new Build({ lvl: DEFAULT_LVL });

const getSlotList = (itemTypeId) => {
    const slotId = getSlotIdForItemType(itemTypeId);
    // Fix: Check for null/undefined explicitly, not falsy (slot 0 is valid!)
    if (slotId === null || slotId === undefined) {
        return EMPTY_LIST;
    }
    return Array.isArray(slotId) ? slotId : [slotId];
};

const getAvailableSlot = (slotList, items, usedSlots) => {
    for (const slot of slotList) {
        if (usedSlots.has(slot)) continue;
        if (slot >= items.length || !items[slot]) {
            return slot;
        }
    }
    for (const slot of slotList) {
        if (!usedSlots.has(slot)) {
            return slot;
        }
    }
    return null;
};

const buildItemNotificationMessage = (itemName, lang) => {
    return itemName ? `${itemName} ${MSG_ITEM_EQUIPPED[lang]}` : MSG_ITEM_EQUIPPED[lang];
};

const buildSetNotificationMessage = (setName, itemsEquipped, lang) => {
    const suffix = `(${itemsEquipped} ${MSG_PIECES[lang]})`;
    return setName ? `${setName} ${MSG_SET_EQUIPPED[lang]} ${suffix}` : `${MSG_SET_EQUIPPED[lang]} ${suffix}`;
};

/**
 * Custom hook for equipping items and sets to the build
 * Centralizes equip functionality so it works from any component
 */
export const useEquipItem = (lang = 'fr') => {
    const { showNotification } = useNotification();
    const { showOverlay, hideOverlay } = useOverlay();
    const { build, setBuild } = useGeneratorContext();

    const equipItemInSlot = useCallback((item, slotId) => {
        if (!item || !item.item_type_id || slotId === null || slotId === undefined) {
            console.error('[Equip Error] equipItemInSlot failed:', {
                hasItem: !!item,
                itemTypeId: item?.item_type_id,
                slotId,
                itemName: item?.name,
                itemId: item?.id
            });
            showNotification(MSG_EQUIP_ERROR[lang], 'error');
            return false;
        }

        // Functional update so equipping several items in quick succession (e.g.
        // clicking through a set overlay) composes against the latest build rather
        // than a stale closure, which would drop items and desync the stat total.
        setBuild(prevBuild => {
            const currentBuild = getBaseBuild(prevBuild);
            const newItems = [...currentBuild.items];
            newItems[slotId] = item;
            return currentBuild.setItems(newItems);
        });

        const itemName = getItemName(item, lang);
        const notificationMessage = buildItemNotificationMessage(itemName, lang);
        showNotification(notificationMessage, 'success');
        return true;
    }, [lang, setBuild, showNotification]);

    const showSlotPicker = useCallback((item, slotList) => {
        const handleSelect = (slotId) => {
            equipItemInSlot(item, slotId);
            hideOverlay();
        };

        const itemName = getItemName(item, lang);
        const itemIcon = item?.icon ? resolveMediaUrl(item.icon) : (getSlotImg(slotList[0]) || '/img/stat_re_armes.png');
        const titleText = TXT_SLOT_SELECTION[lang] || TXT_SLOT_SELECTION.en;

        showOverlay([
            <div key="slot-select-header" className="slot-header title">
                <MediaImg src={itemIcon} alt={itemName || 'Slot'} className="slot-header-icon" resolve={false} />
                <h6>
                    <span>{titleText}</span>
                </h6>
            </div>,
            <SlotSelectOverlay
                key="slot-select-content"
                build={build}
                slotIds={slotList}
                lang={lang}
                onSelect={handleSelect}
            />
        ], { compact: true });
    }, [build, equipItemInSlot, hideOverlay, lang, showOverlay]);

    const equipItem = useCallback((item, preferredSlotId = null) => {
        if (!item || !item.item_type_id) {
            console.error('[Equip Error] equipItem - invalid item:', {
                hasItem: !!item,
                itemTypeId: item?.item_type_id,
                itemName: item?.name,
                itemId: item?.id
            });
            showNotification(MSG_EQUIP_ERROR[lang], 'error');
            return false;
        }

        try {
            const currentBuild = getBaseBuild(build);
            const buildLvl = currentBuild.lvl || DEFAULT_LVL;
            const itemLvl = getItemLevel(item);
            if (itemLvl > buildLvl) {
                const itemName = getItemName(item, lang);
                const label = MSG_ITEM_LEVEL_TOO_HIGH[lang];
                showNotification(`${itemName} — ${label} (${itemLvl} > ${buildLvl})`, 'warning');
                return false;
            }

            const slotList = getSlotList(item.item_type_id);
            if (preferredSlotId !== null && preferredSlotId !== undefined) {
                const isValidSlot = Array.isArray(slotList)
                    ? slotList.includes(preferredSlotId)
                    : slotList === preferredSlotId;

                if (isValidSlot) {
                    return equipItemInSlot(item, preferredSlotId);
                }
            }

            if (slotList.length > MULTI_SLOT_COUNT) {
                showSlotPicker(item, slotList);
                return false;
            }
            const slotId = findAvailableSlotForItem(item.item_type_id, currentBuild.items);

            if (slotId === null) {
                showNotification(MSG_EQUIP_ERROR[lang], 'error');
                return false;
            }

            return equipItemInSlot(item, slotId);
        } catch (error) {
            showNotification(MSG_EQUIP_ERROR[lang], 'error');
            return false;
        }
    }, [build, lang, showNotification, equipItemInSlot, showSlotPicker]);

    const equipSet = useCallback((set, preferredSlotId = null) => {
        if (!set || !set.items || !Array.isArray(set.items) || set.items.length === 0) {
            console.error('[equipSet] Invalid set:', { set, hasItems: !!set?.items, itemsLength: set?.items?.length });
            showNotification(MSG_EQUIP_ERROR[lang], 'error');
            return false;
        }

        try {
            const currentBuild = getBaseBuild(build);
            const buildLvl = currentBuild.lvl || DEFAULT_LVL;

            const newItems = [...currentBuild.items];
            let itemsEquipped = ZERO_COUNT;
            let itemsAlreadyEquipped = ZERO_COUNT;
            let itemsSkippedLevel = ZERO_COUNT;
            const usedSlots = new Set();
            let preferredSlotUsed = false;

            for (const item of set.items) {
                if (!item || !item.item_type_id) {
                    continue;
                }
                if (hasItemInBuild(newItems, item.id)) {
                    itemsAlreadyEquipped += 1;
                    continue;
                }

                const itemLvl = getItemLevel(item);
                if (itemLvl > buildLvl) {
                    itemsSkippedLevel++;
                    continue;
                }

                const slotList = getSlotList(item.item_type_id);
                let slotId = null;

                if (preferredSlotId !== null && preferredSlotId !== undefined && !preferredSlotUsed) {
                    const isValidSlot = Array.isArray(slotList)
                        ? slotList.includes(preferredSlotId)
                        : slotList === preferredSlotId;

                    if (isValidSlot && (preferredSlotId >= newItems.length || !newItems[preferredSlotId])) {
                        slotId = preferredSlotId;
                        preferredSlotUsed = true;
                    }
                }

                if (slotId === null) {
                    slotId = slotList.length > 0
                        ? getAvailableSlot(slotList, newItems, usedSlots)
                        : findAvailableSlotForItem(item.item_type_id, newItems);
                }

                if (slotId !== null) {
                    newItems[slotId] = item;
                    usedSlots.add(slotId);
                    itemsEquipped++;
                } else {
                    console.error('[Equip Error] equipSet - no slot found for item:', {
                        itemTypeId: item.item_type_id,
                        itemName: item.name,
                        itemId: item.id,
                        slotList,
                        usedSlots: Array.from(usedSlots)
                    });
                }
            }

            if (itemsEquipped === ZERO_COUNT) {
                if (itemsSkippedLevel > ZERO_COUNT && itemsAlreadyEquipped === ZERO_COUNT) {
                    const setName = getSetName(set, lang);
                    showNotification(
                        `${setName} — ${itemsSkippedLevel} ${MSG_SET_PARTIAL_LEVEL[lang]}`,
                        'warning'
                    );
                    return false;
                }
                if (itemsAlreadyEquipped > ZERO_COUNT) {
                    const setName = getSetName(set, lang);
                    const alreadyEquippedMsg = TXT_SETNAME_SET_ALREADY_EQUIPPED[lang] || TXT_SETNAME_SET_ALREADY_EQUIPPED.en;
                    showNotification(alreadyEquippedMsg, 'info');
                    return true;
                }
                console.error('[equipSet] No items equipped and none already equipped');
                showNotification(MSG_EQUIP_ERROR[lang], 'error');
                return false;
            }

            let updatedBuild = currentBuild.setItems(newItems);

            let setId = set.id ? String(set.id) : null;
            if (!setId && set.items && set.items.length > 0) {
                const firstItem = set.items.find(item => item && item.item_set_id);
                if (firstItem) {
                    setId = String(firstItem.item_set_id);
                }
            }

            if (setId && set.bonuses && Array.isArray(set.bonuses)) {
                const equippedItemIds = new Set(
                    newItems.filter(item => item && item.id).map(item => String(item.id))
                );

                const activeItems = [];
                const inactiveItems = [];

                (set.items || []).forEach(item => {
                    if (!item || !item.id) return;
                    const itemId = String(item.id);
                    if (equippedItemIds.has(itemId)) {
                        activeItems.push(item);
                    } else {
                        inactiveItems.push(item);
                    }
                });

                const updatedSetBonuses = { ...updatedBuild.setBonuses };
                updatedSetBonuses[setId] = {
                    name: set.name || { fr: setId, en: setId },
                    pieces: activeItems.length,
                    bonuses: set.bonuses,
                    active_items: activeItems,
                    inactive_items: inactiveItems,
                    max_pieces: set.max_pieces || set.items.length
                };
                updatedBuild = updatedBuild.setSetBonuses(updatedSetBonuses);
            }

            setBuild(updatedBuild);

            const setName = getSetName(set, lang);
            const notificationMessage = buildSetNotificationMessage(setName, itemsEquipped, lang);
            showNotification(notificationMessage, 'success');

            if (itemsSkippedLevel > ZERO_COUNT) {
                showNotification(
                    `${itemsSkippedLevel} ${MSG_SET_PARTIAL_LEVEL[lang]}`,
                    'warning'
                );
            }

            return true;
        } catch (error) {
            console.error('Error equipping set:', error);
            showNotification(MSG_EQUIP_ERROR[lang], 'error');
            return false;
        }
    }, [build, lang, setBuild, showNotification]);

    const handleEquip = useCallback((selectedItem) => {
        if (!selectedItem) return;

        // Check if it's a set: has items array or search_result_type is 'set'
        const isSet = selectedItem.search_result_type === 'set' ||
            (Array.isArray(selectedItem.items) && selectedItem.items.length > 0);

        if (isSet) {
            equipSet(selectedItem);
        } else if (selectedItem.item_type_id) {
            // It's an item
            equipItem(selectedItem);
        } else {
            showNotification(MSG_EQUIP_ERROR[lang], 'error');
        }
    }, [equipItem, equipSet, lang, showNotification]);

    return {
        equipItem,
        equipSet,
        handleEquip
    };
};

export default useEquipItem;
